"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

interface StyledModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  className?: string
  maxWidth?: string
  showCloseButton?: boolean
  footer?: React.ReactNode
}

export function StyledModal({
  isOpen,
  onClose,
  title,
  children,
  className,
  maxWidth = "700px",
  showCloseButton = true,
  footer
}: StyledModalProps) {
  // Close on escape key
  React.useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    
    window.addEventListener('keydown', handleEscapeKey)
    return () => window.removeEventListener('keydown', handleEscapeKey)
  }, [isOpen, onClose])

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div 
        className={cn("w-full max-w-full transform transition-all", className)}
        style={{ maxWidth }}
      >
        <div className="relative bg-white dark:bg-[#182438] border border-[#2F2F2F]/10 dark:border-[#333840] rounded-xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[#2F2F2F]/10 dark:border-[#333840] bg-gradient-to-r from-[#439476]/10 to-[rgba(8,31,61,0.05)] dark:from-[#439476]/20 dark:to-[rgba(8,31,61,0.1)]">
            <h2 className="text-xl font-semibold text-[#1C1C1C] dark:text-[#E8E8E8]">
              {title}
            </h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/50 dark:hover:bg-[#1E2B42]/70 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-[#2F2F2F] dark:text-[#A3A3A3]" />
              </button>
            )}
          </div>

          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {children}
          </div>

          {footer && (
            <div className="flex gap-2 p-4 border-t border-[#2F2F2F]/10 dark:border-[#333840] bg-gradient-to-r from-[#F3F4F6]/50 to-transparent dark:from-[#1E2B42]/50 dark:to-transparent">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
}

export function StyledModalSection({
  children,
  className,
  title,
  icon,
  primaryColor = "#439476",
  secondaryColor = "rgba(8,31,61,1)",
  hasBorder = true
}: {
  children: React.ReactNode
  className?: string
  title?: string
  icon?: React.ReactNode
  primaryColor?: string
  secondaryColor?: string
  hasBorder?: boolean
}) {
  const primaryColorLight = `${primaryColor}/10`
  const primaryColorDark = `${primaryColor}/20`
  const secondaryColorLight = secondaryColor.includes('rgba') 
    ? secondaryColor.replace(')', ', 0.05)') 
    : `${secondaryColor}/5`
  const secondaryColorDark = secondaryColor.includes('rgba')
    ? secondaryColor.replace(')', ', 0.1)')
    : `${secondaryColor}/10`
  const borderColor = hasBorder ? `border border-${primaryColor}/20 dark:border-${primaryColor}/30` : ''

  return (
    <div className={cn(
      `bg-gradient-to-r from-[${primaryColorLight}] to-[${secondaryColorLight}] dark:from-[${primaryColorDark}] dark:to-[${secondaryColorDark}] rounded-lg p-4 ${borderColor}`,
      className
    )}>
      {(title || icon) && (
        <div className="flex items-center gap-2 mb-2">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          {title && <h3 className={`text-sm font-medium text-[${primaryColor}] dark:text-[${primaryColor}]`}>{title}</h3>}
        </div>
      )}
      {children}
    </div>
  )
}

export function StyledModalButton({
  children,
  onClick,
  className,
  variant = "primary",
  disabled = false,
  icon,
  fullWidth = false,
  type = "button"
}: {
  children: React.ReactNode
  onClick?: () => void
  className?: string
  variant?: "primary" | "secondary" | "outline" | "danger" | "success"
  disabled?: boolean
  icon?: React.ReactNode
  fullWidth?: boolean
  type?: "button" | "submit" | "reset"
}) {
  const baseClasses = "h-11 font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
  const widthClass = fullWidth ? "flex-1 w-full" : "px-6"
  
  const variantClasses = {
    primary: "bg-gradient-to-r from-[#439476] to-[rgba(8,31,61,1)] hover:from-[#387565] hover:to-[rgba(8,31,61,0.9)] text-white disabled:hover:from-[#439476] disabled:hover:to-[rgba(8,31,61,1)]",
    secondary: "bg-[rgba(8,31,61,0.1)] hover:bg-[rgba(8,31,61,0.2)] text-[rgba(8,31,61,1)] dark:text-[#E8E8E8] border border-[rgba(8,31,61,0.2)] dark:border-[rgba(8,31,61,0.3)]",
    outline: "bg-white dark:bg-[#1E2B42] hover:bg-[#F3F4F6] dark:hover:bg-[#182438] text-[#2F2F2F] dark:text-[#A3A3A3] border border-[#2F2F2F]/10 dark:border-[#333840]",
    danger: "bg-gradient-to-r from-[#E53935] to-[#D32F2F] hover:from-[#F44336] hover:to-[#E53935] text-white",
    success: "bg-gradient-to-r from-[#439476] to-[#2E7D32] hover:from-[#43A047] hover:to-[#388E3C] text-white",
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(baseClasses, widthClass, variantClasses[variant], className)}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  )
}
